import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { TypeToConfirmDialog } from '@optools/components/dialog/type-to-confirm-dialog';
import { Button } from '@optools/components/ui/button';
import { Checkbox } from '@optools/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@optools/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { SheetFooter } from '@optools/components/ui/sheet';
import { Textarea } from '@optools/components/ui/textarea';
import { cn } from '@optools/components/ui/utils';
import { useAccessCheck } from '@optools/hooks/useAccessCheck';
import { Audience } from '@optools/services/operator-gateway/esni/types';
import { AudienceType } from '@optools/services/operator-gateway/types/generated/graphql';
import { selectToolsConfig } from '@optools/store/configApiSlice';
import { useAppSelector } from '@optools/store/hooks';
import { capitalize } from 'es-toolkit';
import { CircleDot, CircleDotDashed, CircleSlash, CircleX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useDetailsParams } from '../hooks/useDetailsParams';
import { AudienceDefinitionUpload } from './audience-definition-upload';
import { AudienceSelect } from './audience-select';
import { ESNIFormPopover } from './esni-form-popover';

export const POPOVER_CONTENT = [
  {
    description: 'All of the audiences or audience definitions are met',
    icon: CircleDot,
    title: 'ALL',
  },
  {
    description: 'One of the audiences or audience definitions are met',
    icon: CircleDotDashed,
    title: 'ANY',
  },
  {
    description: 'None of the audiences or audience definitions are met',
    icon: CircleSlash,
    title: 'NONE',
  },
];

export const formatValues = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const matchOptions = ['ALL', 'ANY', 'NONE'] as const;

const audienceTypes = Object.keys(AudienceType).map((key) => ({
  label: key !== 'IP' ? capitalize(key) : key,
  value: key,
}));

const formSchema = z.object({
  authenticated: z.boolean(),
  href: z.string().min(1, { error: 'Audience href is required' }),
  match: z.string().min(1, { error: 'Match is required' }),
  type: z.enum(AudienceType),
  values: z.string().min(1, { error: 'Audience definition is required' }),
});

export type AudienceFormValues = Omit<z.infer<typeof formSchema>, 'values'> & { values: string[] };

export interface AudienceFormProps {
  audience?: Audience | null;
  disabled?: boolean;
  editMode?: boolean;
  loading: boolean;
  onCancel: () => void;
  onDelete?: (href: string) => void;
  onSubmit: (values: AudienceFormValues) => void;
  updateEditMode?: (mode: boolean) => void;
}

const JOIN_PATTERN = ',\n';

export function AudienceForm({
  audience,
  editMode = true,
  loading,
  onCancel,
  onDelete,
  onSubmit,
  updateEditMode,
}: AudienceFormProps) {
  const { setDetailsParam } = useDetailsParams();
  const {
    authenticated = false,
    details: values = [],
    href = '',
    match = '',
    type = AudienceType.GROUP,
  } = audience || {};

  const {
    esni: {
      auth: {
        pages: {
          audiences: {
            featureGroups: { deleteAudience },
          },
        },
      },
    },
  } = useAppSelector(selectToolsConfig);
  const { checkGroupAccess } = useAccessCheck();

  const form = useForm<z.infer<typeof formSchema>>({
    defaultValues: {
      authenticated,
      href,
      match,
      type,
      values: values?.join(JOIN_PATTERN) ?? '',
    },
    disabled: loading || !editMode,
    resolver: zodResolver(formSchema),
  });

  const typeValue = form.watch('type');
  const audienceDefinitionValue = form.watch('values');

  // Group audience values operate as an array instead of a single string in a text area
  const groupAudienceValue = audienceDefinitionValue.split(JOIN_PATTERN).filter(Boolean);

  const onResetHandler = () => {
    form.reset();
    onCancel();
  };

  const handleSubmit = (formValues: z.infer<typeof formSchema>) => {
    const { values, ...rest } = formValues;
    onSubmit({ ...rest, values: formatValues(values) });
  };

  const addGroupAudience = (audience: string) => {
    const audienceGroups = groupAudienceValue;
    const index = audienceGroups.findIndex((item) => item === audience);

    if (!~index) {
      audienceGroups.push(audience);
    } else {
      audienceGroups.splice(index, 1);
    }

    form.setValue('values', audienceGroups.join(JOIN_PATTERN));
  };

  const removeGroupAudience = (audience: string) => {
    const values = groupAudienceValue.filter((item) => item !== audience);
    form.setValue('values', values.join(JOIN_PATTERN));
  };

  const onSetAudienceType = (newAudienceType: AudienceType) => {
    form.setValue('values', newAudienceType !== type ? '' : (values?.join(JOIN_PATTERN) ?? ''));
  };

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onReset={onResetHandler}
        onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="href"
          render={({ field }) => {
            const disableInput = !editMode || field.disabled || !!audience;
            return (
              <FormItem>
                <FormLabel>Audience Href</FormLabel>
                <FormControl>
                  <Input type="text" {...field} disabled={disableInput} readOnly={disableInput} />
                </FormControl>
                {!!audience && (
                  <FormDescription className="text-xs">Audience Href is readonly</FormDescription>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="match"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Match
                <ESNIFormPopover aria-label="additional match context" content={POPOVER_CONTENT} />
              </FormLabel>

              <FormControl>
                <RadioGroup
                  className={cn({ 'opacity-50': field.disabled }, 'flex flex-wrap gap-4')}
                  disabled={field.disabled}
                  value={field.value}
                  onValueChange={field.onChange}>
                  {matchOptions.map((actionOption) => {
                    return (
                      <FormItem
                        key={actionOption}
                        className={cn({ hidden: !editMode && field.value !== actionOption })}>
                        <FormControl>
                          <div className="flex items-center gap-x-2">
                            <RadioGroupItem id={actionOption} value={actionOption} />
                            <FormLabel htmlFor={actionOption}>{actionOption}</FormLabel>
                          </div>
                        </FormControl>
                      </FormItem>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Audience Type</FormLabel>
              <Select
                disabled={field.disabled}
                value={field.value}
                onValueChange={(value) => {
                  onSetAudienceType(value as AudienceType);
                  field.onChange(value);
                }}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select an Audience Type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {audienceTypes.map((item) => {
                    return (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="values"
          render={({ field, fieldState }) =>
            typeValue !== AudienceType.GROUP ? (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Audience Definition</FormLabel>
                  {editMode && (
                    <AudienceDefinitionUpload
                      onChange={field.onChange}
                      onError={(message) => form.setError('values', { message })}
                    />
                  )}
                </div>
                <FormControl>
                  <Textarea
                    {...field}
                    className="h-56"
                    disabled={field.disabled}
                    value={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
                {fieldState.error ? (
                  <FormMessage />
                ) : (
                  <FormDescription>Definition should be comma separated.</FormDescription>
                )}
              </FormItem>
            ) : (
              <FormItem>
                <FormLabel className="sr-only">Add Audience Groups</FormLabel>
                <div className="flex flex-col gap-y-2">
                  {groupAudienceValue.map((item) => (
                    <div key={item} className="flex w-full gap-x-2">
                      <Button
                        className="flex-1 justify-start font-normal"
                        disabled={editMode}
                        type="button"
                        variant="link"
                        onClick={() => setDetailsParam('audience', item)}>
                        {item}
                      </Button>
                      {editMode && (
                        <ConfirmationDialog
                          description={`Are you sure you wanted to remove ${item}?`}
                          title="Remove Audience"
                          onConfirm={() => removeGroupAudience(item)}>
                          <Button
                            aria-label={`remove ${item}`}
                            className={cn({ hidden: !editMode })}
                            size="icon"
                            type="button"
                            variant="outline">
                            <CircleX />
                          </Button>
                        </ConfirmationDialog>
                      )}
                    </div>
                  ))}
                  <FormControl>
                    <AudienceSelect
                      alwaysShowPlaceholder={true}
                      aria-label="Add Audience"
                      disabled={field.disabled}
                      disableOption={(item) => item.value === href}
                      placeHolder="Add Audience"
                      value={field.value}
                      onValueChange={(value) => addGroupAudience(value)}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )
          }
        />

        <FormField
          control={form.control}
          name="authenticated"
          render={({ field }) => (
            <FormItem className="mt-4 flex items-center gap-2 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  className="peer"
                  disabled={field.disabled}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="cursor-pointer peer-disabled:opacity-50">
                Authenticated
              </FormLabel>
            </FormItem>
          )}
        />

        <SheetFooter>
          {editMode ? (
            <div className="flex gap-2">
              <Button disabled={form.formState.disabled} type="reset" variant="secondary">
                Cancel
              </Button>
              <Button
                className="w-16"
                disabled={form.formState.disabled}
                loading={form.formState.disabled}
                type="submit">
                Save
              </Button>
            </div>
          ) : (
            <>
              <Button disabled={loading} type="button" onClick={() => updateEditMode?.(true)}>
                Edit Audience
              </Button>
              {checkGroupAccess(deleteAudience) && onDelete && (
                <TypeToConfirmDialog
                  actionText="Delete"
                  description="Are you sure you want to delete this Audience?"
                  destructive={true}
                  matchInstructions={
                    <p className="text-sm text-muted-foreground">
                      Type <strong className="text-popover-foreground">{href}</strong> to delete
                    </p>
                  }
                  stringToMatch={href}
                  title="Delete Audience"
                  onConfirm={() => onDelete(href)}>
                  <Button disabled={loading} type="button" variant="destructive">
                    Delete Audience
                  </Button>
                </TypeToConfirmDialog>
              )}
            </>
          )}
        </SheetFooter>
      </form>
    </Form>
  );
}
